/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { User } from "@/models/user.model";
import { authService } from "@/services/authService";
import { cognitoService } from "@/services/cognito.service";
import { smartService } from "@/services/smart.service";
import { clearActiveClinicId } from './ClinicContext';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<User>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  verifyInvite: (token: string) => Promise<void>;
  acceptInvitation: (token: string, password: string, role?: string, clinicId?: string) => Promise<void>;
  fhirLogin: (organizationId: string, userId: string) => Promise<User>;
  selectClinic: (clinicId: string) => Promise<User>;
  updateUserClinic: (clinicId: string, updates: Record<string, any>) => void;
  }

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const localDevBypass = import.meta.env.DEV && import.meta.env.VITE_LOCAL_DEV_AUTH_BYPASS === 'true';

  const LOCAL_DEV_CLINIC_ID = import.meta.env.VITE_LOCAL_DEV_CLINIC_ID || '550e8400-e29b-41d4-a716-446655449201';
  const LOCAL_DEV_CLINIC_SLUG = import.meta.env.VITE_LOCAL_DEV_CLINIC_SLUG || 'local-dev-clinic';

  const checkAuth = async () => {
    try {
      if (localDevBypass) {
        // Local integration testing: skip Cognito but try /clinic/me for real data.
        // Backend should be started with LOCAL_DEV_AUTH_BYPASS=true.
        // Set a dummy token so API calls work
        if (!localStorage.getItem('authToken')) {
          localStorage.setItem('authToken', 'local-dev-bypass-token');
        }
        // Ensure the app has a selected clinic so ClinicContext can load.
        if (!localStorage.getItem('selectedClinicId')) {
          localStorage.setItem('selectedClinicId', LOCAL_DEV_CLINIC_ID);
          localStorage.setItem('selectedClinicTimestamp', Date.now().toString());
        }

        // Try to fetch real user data from /clinic/me (backend bypass accepts any token)
        try {
          const currentUser = await authService.getCurrentUser();
          if (currentUser?.data?.user) {
            setUser(currentUser.data.user);
            return;
          }
        } catch (meError) {
          console.warn('Local dev bypass: /clinic/me failed, using hardcoded user', meError);
        }

        // Fallback to hardcoded user if /clinic/me is unavailable
        setUser({
          id: '550e8400-e29b-41d4-a716-446655440001',
          role: 'admin',
          email: 'superadmin@novamed.app',
          first_name: 'Super',
          last_name: 'Admin',
          hasMultipleClinics: false,
          clinic: {
            id: LOCAL_DEV_CLINIC_ID,
            name: import.meta.env.VITE_LOCAL_DEV_CLINIC_NAME || 'Local Dev Clinic',
            clinic_slug: LOCAL_DEV_CLINIC_SLUG,
            clinic_archetype: 'guided',
            is_trial: import.meta.env.VITE_LOCAL_DEV_TRIAL_MODE === 'true',
            trial_ends_at: import.meta.env.VITE_LOCAL_DEV_TRIAL_MODE === 'true'
              ? new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
              : undefined,
          },
        } as unknown as User);
        return;
      }

      const token = localStorage.getItem("authToken");
      if (token) {
        // Check if there's a selected clinic in localStorage
        const selectedClinicId = localStorage.getItem('selectedClinicId');
        
        // Get current user - if selected clinic exists, it will be used by backend
        const currentUser = await authService.getCurrentUser();
        const userData = currentUser.data.user;
        
        // If practitioner has multiple clinics but no clinic selected, don't set clinic
        if (userData.hasMultipleClinics && !selectedClinicId) {
          // User needs to select a clinic
          userData.clinic = undefined;
        }
        
        setUser(userData);
      }
    } catch (error) {
      console.error("Authentication check failed:", error);
      localStorage.removeItem("authToken");
      localStorage.removeItem('accessToken');
      localStorage.removeItem('selectedClinicId');
      localStorage.removeItem('selectedClinicTimestamp');
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<User> => {
    try {
      // First authenticate with Cognito
      const cognitoResponse = await cognitoService.login(email, password);
      const { accessToken, idToken, refreshToken } = cognitoResponse;
      localStorage.setItem('authToken', idToken);
      localStorage.setItem('accessToken', accessToken);
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      }
      
      // Get user data from backend (getCurrentUser now returns clinics for practitioners)
      const currentUser = await authService.getCurrentUser();
      const userData = currentUser.data.user;
      
      setUser(userData);
      return userData;
    } catch (error) {
      throw error;
    }
  };
  const selectClinic = async (clinicId: string): Promise<User> => {
    try {
      if (!clinicId) {
        // If no clinicId provided, just return current user (for loading clinics)
        const currentUser = await authService.getCurrentUser();
        setUser(currentUser.data.user);
        return currentUser.data.user;
      }
      
      const response = await authService.selectClinic(clinicId);
      const updatedUser = response.data.user;
      
      // Store selected clinic in localStorage
      localStorage.setItem('selectedClinicId', clinicId);
      localStorage.setItem('selectedClinicTimestamp', Date.now().toString());
      
      setUser(prev => ({ ...prev, ...updatedUser }));
      return updatedUser;
    } catch (error) {
      console.error('Select clinic failed:', error);
      throw error;
    }
  };

  const fhirLogin = async (organizationId: string, userId: string) => {
    try {
      const response = await smartService.authenticateFHIRUser(organizationId, userId);
      if (!response.success) {
        throw new Error(response?.message || 'FHIR login failed');
      }
      const { accessToken, idToken, refreshToken } = response.data;
      localStorage.setItem('authToken', idToken);
      localStorage.setItem('accessToken', accessToken);
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser.data.user);
      return currentUser.data.user;
    } catch (error) {
      console.error("FHIR login failed:", error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      localStorage.removeItem('currentRole');
      localStorage.removeItem('user');
      localStorage.removeItem("authToken");
      localStorage.removeItem('accessToken');
      localStorage.removeItem('selectedClinicId');
      localStorage.removeItem('selectedClinicTimestamp');
      clearActiveClinicId();
      setUser(null);
    }
  };

  const forgotPassword = async (email: string) => {
    try {
      await authService.forgotPassword(email);
    } catch (error) {
      console.error("Forgot password failed:", error);
      throw error;
    }
  };

  const resetPassword = async (token: string, password: string) => {
    try {
      await authService.resetPassword(token, password);
    } catch (error) {
      console.error("Reset password failed:", error);
      throw error;
    }
  };

  const verifyInvite = async (token: string) => {
    try {
      await authService.verifyInvite(token);
    } catch (error) {
      console.error("Verify invite failed:", error);
      throw error;
    }
  };

    const acceptInvitation = async (token: string, password: string, role?: string, clinicId?: string) => {
    try {
      await authService.acceptInvitation(token, password, role, clinicId);
    } catch (error) {
      console.error("Accept invitation failed:", error);
      throw error;
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const updateUserClinic = (clinicId: string, updates: Record<string, any>) => {
    setUser(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        clinic: prev.clinic?.id === clinicId ? { ...prev.clinic, ...updates } : prev.clinic,
        clinics: prev.clinics?.map((c: any) => c.id === clinicId ? { ...c, ...updates } : c),
      };
    });
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    checkAuth,
    forgotPassword,
    resetPassword,
    verifyInvite,
    acceptInvitation,
    fhirLogin,
    selectClinic,
    updateUserClinic,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
